import React from 'react';
import BellIcon from './icons/BellIcon';
import XIcon from './icons/XIcon';
import CheckCircle2 from './icons/CheckCircle2';
import type { Language, UserNotification } from '../types';
import { getTranslation } from '../utils/translations';

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
    notifications: UserNotification[];
    language?: Language;
    onMarkAllRead: () => void;
    onNotificationClick: (notification: UserNotification) => void;
}

function formatNotificationTime(createdAt: string, language?: Language): string {
    try {
        return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(new Date(createdAt));
    } catch {
        return createdAt;
    }
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
    isOpen,
    onClose,
    notifications,
    language,
    onMarkAllRead,
    onNotificationClick,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const hasUnread = notifications.some((notification) => !notification.read);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[160] p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 rounded-t-xl">
                    <div className="flex items-center gap-3 text-slate-800 dark:text-slate-100">
                        <BellIcon className="h-6 w-6" />
                        <h2 className="text-xl font-bold">{t('notifications')}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasUnread && (
                            <button
                                type="button"
                                onClick={onMarkAllRead}
                                className="text-xs font-bold text-brand-sage hover:text-brand-sage/80 px-2 py-1 rounded-lg"
                            >
                                {t('markAllRead')}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 p-1"
                        >
                            <XIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <div className="p-4 overflow-y-auto flex-grow space-y-3">
                    {notifications.length > 0 ? (
                        notifications.map((notification) => {
                            const isSuccess = notification.type === 'share_import_success';
                            const isDuplicate = notification.type === 'share_import_duplicate';
                            const isStarted = notification.type === 'share_import_started';
                            const accentClass = isSuccess
                                ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10'
                                : isDuplicate
                                    ? 'border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-900/10'
                                    : isStarted
                                        ? 'border-sky-200 dark:border-sky-900/40 bg-sky-50/50 dark:bg-sky-900/10'
                                    : 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10';

                            return (
                                <button
                                    key={notification.id}
                                    type="button"
                                    onClick={() => onNotificationClick(notification)}
                                    className={`w-full text-start p-4 rounded-xl border shadow-sm transition-all hover:shadow-md ${accentClass} ${notification.read ? 'opacity-70' : ''}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-slate-800 dark:text-slate-100 leading-snug">
                                                {notification.title}
                                            </p>
                                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                                                {notification.body}
                                            </p>
                                        </div>
                                        {!notification.read && (
                                            <span className="shrink-0 w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5" />
                                        )}
                                    </div>
                                    <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-3">
                                        {formatNotificationTime(notification.createdAt, language)}
                                    </p>
                                </button>
                            );
                        })
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400 dark:text-slate-500">
                            <CheckCircle2 className="w-10 h-10 mb-3 opacity-40" />
                            <p className="font-medium">{t('notificationsEmpty')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsModal;
